import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Top-level error boundary — the backstop that turns an unhandled
 * render error into a recoverable card instead of a white screen.
 *
 * Added after the "unknown alertType white-screens the whole app"
 * bug (post-audit remediation Tier 1.1): render-safety at call
 * sites is the first line of defense; this is the last.
 *
 * Class component because React only supports error boundaries via
 * getDerivedStateFromError/componentDidCatch — there is no hook
 * equivalent.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console is the V1 sink — an error-reporting service can hook
    // in here later without touching call sites.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-kw-bg p-6">
          <div className="w-full max-w-md rounded-kw-xl border border-kw-border bg-white p-6 shadow-kw-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 size-6 shrink-0 text-kw-danger-text" />
              <div>
                <p className="text-[15px] font-semibold text-kw-shark">Something went wrong</p>
                <p className="mt-1 text-[13px] text-kw-muted">
                  The page hit an unexpected error. Your data is fine — this is a display problem. Try again,
                  or reload the page if it persists.
                </p>
                <p className="mt-2 break-words font-mono text-[11px] text-kw-muted">
                  {this.state.error.message}
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={this.handleReset}
                    className="rounded-kw-md bg-kw-cerulean px-3 py-1.5 text-[13px] font-medium text-white hover:bg-kw-cerulean/90"
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      window.location.reload();
                    }}
                    className="rounded-kw-md border border-kw-border px-3 py-1.5 text-[13px] text-kw-shark hover:bg-kw-bg"
                  >
                    Reload
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
